import { Box, Heading, Text, SimpleGrid, HStack, Badge, LinkBox, LinkOverlay } from '@chakra-ui/react';
import { Link as RouterLink } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../features/auth/AuthProvider';
import { useSubscription } from '../features/subscription/hooks/useSubscription';

interface CardDef {
  key: string;
  to?: string;
}

const LIVE_CARDS: CardDef[] = [
  { key: 'library', to: '/library' },
  { key: 'scenarios', to: '/scenarios' },
  { key: 'schedule', to: '/schedule' },
  { key: 'player', to: '/player' },
  { key: 'locations', to: '/organization/locations' },
  { key: 'alerts', to: '/alerts' },
  { key: 'team', to: '/organization/users' },
  { key: 'organization', to: '/organization/settings' },
  { key: 'auditLog', to: '/organization/audit-log' },
  { key: 'settings', to: '/settings' },
];

// Upcoming parity features (mobile → web). Rendered disabled so the roadmap is visible.
const COMING_SOON_CARDS: CardDef[] = [];

function QuickCard({ to, title, desc }: { to: string; title: string; desc: string }) {
  return (
    <LinkBox
      p={5}
      borderWidth="1px"
      borderColor="gray.200"
      borderRadius="lg"
      bg="white"
      transition="all 0.15s"
      _hover={{ borderColor: 'brand.300', shadow: 'sm' }}
    >
      <LinkOverlay as={RouterLink} to={to}>
        <Text fontWeight="600" color="gray.800">
          {title}
        </Text>
      </LinkOverlay>
      <Text fontSize="sm" color="gray.500" mt={1}>
        {desc}
      </Text>
    </LinkBox>
  );
}

function ComingSoonCard({ title, desc, label }: { title: string; desc: string; label: string }) {
  return (
    <Box p={5} borderWidth="1px" borderStyle="dashed" borderColor="gray.200" borderRadius="lg" bg="white" opacity={0.7}>
      <HStack justify="space-between" align="start">
        <Text fontWeight="600" color="gray.700">
          {title}
        </Text>
        <Badge colorScheme="purple" fontSize="0.65rem">
          {label}
        </Badge>
      </HStack>
      <Text fontSize="sm" color="gray.500" mt={1}>
        {desc}
      </Text>
    </Box>
  );
}

export function DashboardPage() {
  const { t } = useTranslation();
  const { authUser } = useAuth();
  const { isTrialing, daysRemaining, currentPlan, isRestricted, isLocked } = useSubscription();

  const statusLine = isLocked
    ? { text: t('dashboard.statusLocked'), color: 'red.500' }
    : isRestricted
      ? { text: t('dashboard.statusGrace'), color: 'orange.500' }
      : isTrialing
        ? { text: t('dashboard.trialActive', { days: daysRemaining }), color: 'brand.600' }
        : null;

  return (
    <Box>
      <Heading size="lg" color="gray.800">
        {authUser?.fullName ? `${t('dashboard.title')}, ${authUser.fullName.split(' ')[0]}` : t('dashboard.title')}
      </Heading>
      <Text color="gray.500" mt={1}>
        {t('dashboard.subtitle')}
      </Text>

      <HStack mt={3} spacing={3} flexWrap="wrap">
        {currentPlan && (
          <Badge colorScheme="blue" px={2} py={1} borderRadius="md">
            {t('dashboard.plan', { plan: currentPlan })}
          </Badge>
        )}
        {statusLine && (
          <Text fontSize="sm" fontWeight="600" color={statusLine.color}>
            {statusLine.text}
          </Text>
        )}
      </HStack>

      <Text mt={8} mb={3} fontSize="sm" fontWeight="700" color="gray.500" textTransform="uppercase" letterSpacing="wider">
        {t('dashboard.quickLinks')}
      </Text>
      <SimpleGrid columns={{ base: 1, sm: 2, lg: 3 }} spacing={4}>
        {LIVE_CARDS.map((c) => (
          <QuickCard
            key={c.key}
            to={c.to!}
            title={t(`dashboard.cards.${c.key}.title`)}
            desc={t(`dashboard.cards.${c.key}.desc`)}
          />
        ))}
      </SimpleGrid>

      {COMING_SOON_CARDS.length > 0 && (
        <>
          <Text mt={10} mb={1} fontSize="sm" fontWeight="700" color="gray.500" textTransform="uppercase" letterSpacing="wider">
            {t('dashboard.comingSoon')}
          </Text>
          <Text mb={3} fontSize="sm" color="gray.400">
            {t('dashboard.comingSoonNote')}
          </Text>
          <SimpleGrid columns={{ base: 1, sm: 2, lg: 4 }} spacing={4}>
            {COMING_SOON_CARDS.map((c) => (
              <ComingSoonCard
                key={c.key}
                title={t(`dashboard.cards.${c.key}.title`)}
                desc={t(`dashboard.cards.${c.key}.desc`)}
                label={t('dashboard.comingSoon')}
              />
            ))}
          </SimpleGrid>
        </>
      )}
    </Box>
  );
}

export default DashboardPage;
